import React from 'react'
import styled from 'styled-components'
import moment from 'moment'
import ProfilePhoto from '../../../../../components/ProfilePhoto'
import TooltipWrapper from '../../../../../components/TooltipWrapper'
import { useInboxHandlers } from '../../../../../services/actions/inbox/useInboxHandlers'
import SharedAttachment from './SharedAttachment'

interface IMessageItemProps {
    message: any
}

const LinkedInIcon = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
    </svg>
)

const MessageItem: React.FC<IMessageItemProps> = ({ message }) => {
    const { visitParticipantLinkedInProfileHandler, resendMessage } = useInboxHandlers()

    const senderName = [message.sender?.firstName, message.sender?.lastName].filter(Boolean).join(' ')
    /**
     * Shared inbox: LinkedIn shows the account as the sender of every outbound message; this
     * names the teammate who actually wrote it — only when that is someone other than the
     * account owner, or it just repeats the name.
     */
    const writerFullName = [message.sentBy?.firstName, message.sentBy?.lastName].filter(Boolean).join(' ')
    const writerName = message.isSent && writerFullName && writerFullName !== senderName ? writerFullName : ''
    const senderInitials = [message.sender?.firstName?.[0], message.sender?.lastName?.[0]].filter(Boolean).join('')
    const canOpenLinkedIn = !!message.sender?.publicIdentifier

    const replaceToClickableLinks = (text: string) => {
        const urlRegex = /(https?:\/\/[^\s]+)/g
        return text.split(urlRegex).map((part, i) =>
            urlRegex.test(part)
                ? <TextLink key={i} href={part} target="_blank" rel="noopener noreferrer">{part}</TextLink>
                : part
        )
    }

    const renderAttachment = (attachment: any) => {
        // Shared inbox: bytes come through our API (see SharedAttachment); optimistic sends still carry a local reference.
        if (attachment.fetchPath && !attachment.reference) {
            return <SharedAttachment attachment={attachment} sent={!!message.isSent} />
        }
        const type = attachment.mediaType?.split('/')?.[0]
        if (type === 'image') {
            return (
                <img
                    src={attachment.reference}
                    alt={attachment.name}
                    style={{ maxWidth: 280, maxHeight: 280, borderRadius: 10, marginBottom: 6, display: 'block', cursor: 'pointer' }}
                    onClick={() => window.open(attachment.reference, '_blank')}
                />
            )
        }
        return (
            <AttachmentBubble sent={message.isSent}>
                <DownloadIcon onClick={() => window.open(attachment.reference, '_blank')}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                        <polyline points="7 10 12 15 17 10"/>
                        <line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                </DownloadIcon>
                {attachment.name}
                <AttachmentSize> ({(attachment.byteSize / 1000).toFixed(2)} KB)</AttachmentSize>
            </AttachmentBubble>
        )
    }

    const timeLabel = moment(message.dateTimestamp).isBefore(moment(), 'day')
        ? moment(message.dateTimestamp).format('MMM D, YYYY HH:mm')
        : moment(message.dateTimestamp).format('HH:mm')

    return (
        <MessageRow sent={message.isSent}>
            {!message.isSent && (
                <AvatarCol>
                    <TooltipWrapper
                        tooltipText={canOpenLinkedIn ? `Open ${senderName} on LinkedIn` : ''}
                        placement="top"
                    >
                        <AvatarWrap
                            clickable={canOpenLinkedIn}
                            onClick={canOpenLinkedIn ? () => visitParticipantLinkedInProfileHandler(message.sender) : undefined}
                        >
                            <ProfilePhoto
                                source={message.sender?.profilePicture}
                                capitalLetters={senderInitials || 'NN'}
                                allowTooltip={false}
                                width="32px"
                                height="32px"
                            />
                            {canOpenLinkedIn && (
                                <LinkedInBadge>
                                    <LinkedInIcon />
                                </LinkedInBadge>
                            )}
                        </AvatarWrap>
                    </TooltipWrapper>
                </AvatarCol>
            )}

            <MessageContent sent={message.isSent}>
                <ContentWrapper>
                    {message.isFailed && (
                        <TooltipWrapper tooltipText="Resend message" placement="top">
                            <ResendButton onClick={() => resendMessage(message)}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="1 4 1 10 7 10"/>
                                    <path d="M3.51 15a9 9 0 1 0 .49-3.51"/>
                                </svg>
                            </ResendButton>
                        </TooltipWrapper>
                    )}
                    {message?.text && (
                        <Bubble sent={message.isSent}>
                            {replaceToClickableLinks(message.text)}
                        </Bubble>
                    )}
                    {message?.gif?.url && (
                        <img src={message.gif.url} alt="GIF" style={{ maxWidth: 260, borderRadius: 10, marginBottom: 6, display: 'block' }} />
                    )}
                    {message?.forwardedMessage && (
                        <ForwardedBlock sent={message.isSent}>
                            {message.forwardedMessage?.text && <span>{replaceToClickableLinks(message.forwardedMessage.text)}</span>}
                            <ForwardMeta>
                                Forwarded · {[message.forwardedMessage?.from?.firstName, message.forwardedMessage?.from?.lastName].filter(Boolean).join(' ')}
                            </ForwardMeta>
                        </ForwardedBlock>
                    )}
                    {message?.attachments?.map((att: any, i: number) => (
                        <React.Fragment key={i}>{renderAttachment(att)}</React.Fragment>
                    ))}
                    {message?.audioAttachments?.map((att: any, i: number) => (
                        <audio key={i} controls style={{ marginBottom: 6, maxWidth: 260 }}>
                            <source src={att.audioMetadata?.url} />
                        </audio>
                    ))}
                </ContentWrapper>

                <MetaRow sent={message.isSent}>
                    {!message.isFailed && message.sender && senderName && (
                        <TooltipWrapper tooltipText={canOpenLinkedIn ? 'Open on LinkedIn' : ''} placement="top">
                            <SenderName
                                clickable={canOpenLinkedIn}
                                onClick={canOpenLinkedIn ? () => visitParticipantLinkedInProfileHandler(message.sender) : undefined}
                            >
                                {senderName}
                                {canOpenLinkedIn && <LiIconWrap><LinkedInIcon /></LiIconWrap>}
                                {writerName && <SentByText> · written by {writerName}</SentByText>}
                                {' · '}
                            </SenderName>
                        </TooltipWrapper>
                    )}
                    <TimeText failed={message.isFailed}>{timeLabel}</TimeText>
                    {message.isFailed && <FailedText> · Not delivered</FailedText>}
                    {message.isSent && !message.isFailed && (
                        <CheckMark delivered={message.isDelivered}>
                            {message.isDelivered ? '✓✓' : '✓'}
                        </CheckMark>
                    )}
                </MetaRow>
            </MessageContent>

            {message.isSent && (
                <AvatarCol sent>
                    <ProfilePhoto
                        source={message.sender?.profilePicture}
                        capitalLetters={senderInitials || 'ME'}
                        allowTooltip={false}
                        width="32px"
                        height="32px"
                    />
                </AvatarCol>
            )}
        </MessageRow>
    )
}

const MessageRow = styled.div<{ sent?: boolean }>`
    display: flex;
    align-items: flex-end;
    gap: 8px;
    justify-content: ${props => props.sent ? 'flex-end' : 'flex-start'};
    margin-bottom: 10px;
`
const AvatarCol = styled.div<{ sent?: boolean }>`
    flex-shrink: 0;
    align-self: flex-end;
    position: relative;
`
const AvatarWrap = styled.div<{ clickable?: boolean }>`
    position: relative;
    cursor: ${props => props.clickable ? 'pointer' : 'default'};

    &:hover > div:last-child {
        opacity: ${props => props.clickable ? 1 : 0};
    }
`
const LinkedInBadge = styled.div`
    position: absolute;
    bottom: -2px;
    right: -2px;
    width: 14px;
    height: 14px;
    background: #0077b5;
    border-radius: 3px;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.15s;
    color: white;
    pointer-events: none;
`
const MessageContent = styled.div<{ sent?: boolean }>`
    display: flex;
    flex-direction: column;
    align-items: ${props => props.sent ? 'flex-end' : 'flex-start'};
    max-width: 72%;
`
const ContentWrapper = styled.div`
    display: flex;
    flex-direction: column;
`
const Bubble = styled.p<{ sent?: boolean }>`
    display: inline-block;
    font-family: 'Nunito Sans', sans-serif;
    border-radius: ${props => props.sent ? '16px 16px 6px 16px' : '16px 16px 16px 6px'};
    line-height: 22.75px;
    font-size: 14px;
    color: ${props => props.sent ? '#fff' : '#1c212c'};
    background: ${props => props.sent ? '#6467f2' : '#ffffff'};
    border: 1px solid ${props => props.sent ? 'rgba(0,0,0,0)' : 'rgba(232,234,237,0.6)'};
    box-shadow: 0px 1px 1px rgba(0,0,0,0.05);
    white-space: pre-wrap;
    word-break: break-word;
    padding: 11px 17px;
    margin: 0 0 3px;
`
const TextLink = styled.a`
    font-weight: 600;
    color: inherit;
    text-decoration: underline;
    text-underline-offset: 2px;
    &:hover { opacity: 0.8; }
`
const AttachmentBubble = styled.div<{ sent?: boolean }>`
    display: flex;
    align-items: center;
    gap: 8px;
    border-radius: 12px;
    padding: 10px 14px;
    font-size: 13px;
    font-weight: 600;
    background: ${props => props.sent ? '#5964ff' : '#f0f4f8'};
    color: ${props => props.sent ? '#fff' : props.theme.primaryColor};
    margin-bottom: 4px;
`
const DownloadIcon = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    color: inherit;
    opacity: 0.8;
    &:hover { opacity: 1; }
`
const AttachmentSize = styled.span`
    color: rgba(255,255,255,0.7);
    font-weight: 400;
`
const ForwardedBlock = styled.div<{ sent?: boolean }>`
    border-left: 2px solid ${props => props.sent ? 'rgba(255,255,255,0.4)' : '#c0c8d8'};
    padding-left: 10px;
    margin-bottom: 4px;
    font-size: 13px;
    color: ${props => props.sent ? '#fff' : props.theme.primaryColor};
`
const ForwardMeta = styled.p`
    font-size: 11px;
    color: #8c96ad;
    margin: 4px 0 0;
`
const MetaRow = styled.div<{ sent?: boolean }>`
    display: flex;
    align-items: center;
    gap: 2px;
    flex-direction: ${props => props.sent ? 'row-reverse' : 'row'};
    height: 18px;
`
const SenderName = styled.span<{ clickable?: boolean }>`
    font-size: 11px;
    font-weight: 600;
    color: #8c96ad;
    display: flex;
    align-items: center;
    gap: 3px;
    cursor: ${props => props.clickable ? 'pointer' : 'default'};
    transition: color 0.12s;
    &:hover { color: ${props => props.clickable ? '#0077b5' : '#8c96ad'}; }
`
const LiIconWrap = styled.span`
    display: inline-flex;
    align-items: center;
    color: #0077b5;
    opacity: 0.7;
`
const TimeText = styled.span<{ failed?: boolean }>`
    font-family: Menlo, ui-monospace, SFMono-Regular, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
    font-size: 10px;
    line-height: 15px;
    color: ${props => props.failed ? '#ff6760' : '#6a7181'};
`
const SentByText = styled.span`
    font-size: 11px;
    line-height: 15px;
    color: #6467f2;
    font-weight: 600;
    white-space: pre;
`
const FailedText = styled.span`
    font-size: 11px;
    color: #ff6760;
`
const CheckMark = styled.span<{ delivered?: boolean }>`
    font-family: Menlo, ui-monospace, SFMono-Regular, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
    font-size: 10px;
    line-height: 15px;
    color: ${props => props.delivered ? '#6467f2' : '#6a7181'};
    margin-left: 2px;
`
const ResendButton = styled.button`
    background: none;
    border: 1px solid #dae0e8;
    border-radius: 50%;
    width: 28px;
    height: 28px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #8c96ad;
    align-self: center;
    margin-right: 6px;
    transition: border-color 0.12s, color 0.12s;
    &:hover { border-color: #5964ff; color: #5964ff; }
`

export default MessageItem
